import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { COUNTRY_CODES, BRAND } from '../mock/mock';
import { ArrowRight, CheckCircle2, ArrowLeft, Upload, FileText, Trash2, IdCard, ClipboardCheck } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { api } from '../lib/api';

const PortalHeader = () => (
  <header className="border-b border-[#0B5E2A]/10 bg-white/80 backdrop-blur sticky top-0 z-40">
    <div className="max-w-4xl mx-auto container-px h-[64px] flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3">
        <img src={BRAND.logo} alt="HCC" className="h-9 w-9 rounded-full" />
        <div className="leading-tight">
          <div className="font-display text-[15px] font-bold brand-green">{BRAND.name}</div>
          <div className="text-[9px] tracking-[0.22em] uppercase text-brand-gold font-semibold">{BRAND.full}</div>
        </div>
      </Link>
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#4b5e4d] hover:text-[#0B5E2A] transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to portal
      </Link>
    </div>
  </header>
);

const ACCEPT = '.pdf,.jpg,.jpeg,.png,image/*,application/pdf';

const DocSlot = ({ title, hint, file, onPick, onClear, testid }) => (
  <div
    data-testid={`${testid}-slot`}
    className={`border rounded-xl p-4 transition-colors ${file ? 'border-[#0B5E2A]/40 bg-[#f5f9f3]' : 'border-[#0B5E2A]/15 bg-white'}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-[#0B5E2A] flex items-center gap-1.5">
          {title}
          <span className="text-[10px] font-bold uppercase text-[#9b1c1c] bg-[#FDE2E2] rounded px-1.5 py-0.5">Required</span>
        </div>
        {hint && <div className="text-[11px] text-[#6b7e6d] mt-0.5">{hint}</div>}
      </div>
      <label className="shrink-0 cursor-pointer inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-[#0B5E2A]/25 text-[#0B5E2A] hover:bg-[#edf6ea] transition-colors">
        <Upload className="h-3.5 w-3.5" /> {file ? 'Replace' : 'Upload'}
        <input
          type="file"
          className="hidden"
          accept={ACCEPT}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ''; }}
          data-testid={`${testid}-input`}
        />
      </label>
    </div>
    {file ? (
      <div className="mt-3 flex items-center gap-2 bg-white border border-[#0B5E2A]/10 rounded-md px-2.5 py-1.5">
        <FileText className="h-3.5 w-3.5 brand-green shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="truncate text-xs font-medium">{file.name}</div>
          <div className="text-[10px] text-[#6b7e6d]">{Math.ceil(file.size / 1024)} KB</div>
        </div>
        <button
          type="button"
          data-testid={`${testid}-remove`}
          onClick={onClear}
          className="h-6 w-6 rounded text-[#9b1c1c] hover:bg-[#FDE2E2] flex items-center justify-center"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    ) : (
      <div className="mt-3 text-[11px] text-[#6b7e6d] italic">PDF, JPG, or PNG · up to 10 MB</div>
    )}
  </div>
);

const RecruiterSignup = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '', code: '+1', country: '', city: '', years: '', notes: '',
    agree: false, verified: false,
  });
  const [idDoc, setIdDoc] = useState(null);
  const [licenseDoc, setLicenseDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const submit = async (e) => {
    e.preventDefault();
    const req = ['name', 'company', 'email', 'phone', 'country', 'city', 'years'];
    if (req.some((k) => !form[k])) { toast({ title: 'Missing fields', description: 'Please fill all required fields.' }); return; }
    if (!idDoc) { toast({ title: 'ID required', description: 'Please upload a passport, driving license, or national ID.' }); return; }
    if (!licenseDoc) { toast({ title: 'Business license required', description: 'Please upload a copy of your business license.' }); return; }
    if (!form.agree) { toast({ title: 'Agree to the terms', description: 'Please accept the terms & conditions.' }); return; }
    if (!form.verified) { toast({ title: 'Verification required', description: 'Please confirm the verification acknowledgement.' }); return; }

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('company', form.company);
      fd.append('email', form.email);
      fd.append('phone_code', form.code);
      fd.append('phone', form.phone);
      fd.append('country', form.country);
      fd.append('city', form.city);
      fd.append('years', String(form.years));
      fd.append('notes', form.notes || '');
      fd.append('verified', 'true');
      fd.append('id_document', idDoc);
      fd.append('business_license', licenseDoc);
      await api.post('/recruiters', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDone(true);
      toast({ title: 'Application submitted', description: 'Our partnerships team will review and reach out shortly.' });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Submission failed. Try again.' });
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <main className="min-h-screen bg-[#fdfaf1]">
        <PortalHeader />
        <div className="py-20">
          <div className="max-w-xl mx-auto container-px text-center bg-white border border-[#0B5E2A]/10 rounded-2xl p-10">
            <div className="h-16 w-16 rounded-full bg-brand-green-light mx-auto flex items-center justify-center">
              <CheckCircle2 className="h-9 w-9 brand-green" />
            </div>
            <h2 className="mt-5 font-display text-3xl text-[#0B5E2A]">Thanks for Applying</h2>
            <p className="mt-3 text-[#4b5e4d]">Your partnership application and documents have been received. Once approved, you'll get access to the secure recruiter portal.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/" className="btn-green inline-flex items-center gap-2 px-6 py-3 rounded-md">Back to Portal</Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#fdfaf1]">
      <PortalHeader />
      <section className="bg-[#f5f9f3] border-b border-[#0B5E2A]/10">
        <div className="max-w-4xl mx-auto container-px py-14">
          <span className="eyebrow">Recruiter Application</span>
          <h1 className="mt-4 font-display text-4xl md:text-5xl text-[#0B5E2A]">Become a Recruitment Partner</h1>
          <p className="mt-3 text-[#4b5e4d]">Complete the form below and upload your identification and business license. Our team will review your application and contact you shortly.</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-3xl mx-auto container-px">
          <form onSubmit={submit} className="bg-white border border-[#0B5E2A]/10 rounded-2xl p-7 shadow-sm space-y-6">
            {/* Contact block */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full Name *" name="name" value={form.name} onChange={onChange} />
              <Field label="Company Name *" name="company" value={form.company} onChange={onChange} />
              <Field label="Email Address *" name="email" type="email" value={form.email} onChange={onChange} />
              <div>
                <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Phone Number *</label>
                <div className="flex gap-2">
                  <select name="code" value={form.code} onChange={onChange} className="w-[130px] px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30">
                    {COUNTRY_CODES.map((c) => <option key={c.label} value={c.code}>{c.label}</option>)}
                  </select>
                  <input name="phone" value={form.phone} onChange={onChange} className="flex-1 px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30" />
                </div>
              </div>
              <Field label="Country *" name="country" value={form.country} onChange={onChange} />
              <Field label="City *" name="city" value={form.city} onChange={onChange} />
              <Field label="Years of Experience *" name="years" type="number" value={form.years} onChange={onChange} />
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Message / Notes</label>
                <textarea name="notes" value={form.notes} onChange={onChange} rows={4} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30 text-sm resize-none" />
              </div>
            </div>

            {/* Documents */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <IdCard className="h-4 w-4 brand-green" />
                <h3 className="font-display text-lg text-[#0B5E2A]">Required Documents</h3>
              </div>
              <p className="text-xs text-[#6b7e6d] mb-3">Upload clear scans or photos. Files are transmitted securely and visible only to Heritage Community College admissions staff.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <DocSlot
                  title="Government-issued ID"
                  hint="Passport, driving license, or national ID"
                  file={idDoc}
                  onPick={setIdDoc}
                  onClear={() => setIdDoc(null)}
                  testid="recruiter-signup-id"
                />
                <DocSlot
                  title="Business License"
                  hint="Certificate of incorporation or trade license"
                  file={licenseDoc}
                  onPick={setLicenseDoc}
                  onClear={() => setLicenseDoc(null)}
                  testid="recruiter-signup-license"
                />
              </div>
            </div>

            {/* Acknowledgements */}
            <div className="rounded-xl border border-[#F0B93C]/40 bg-[#FEF8E6] p-4 space-y-3">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4 text-[#8a5e00]" />
                <h3 className="font-display text-sm font-semibold text-[#8a5e00] uppercase tracking-wider">Acknowledgements</h3>
              </div>
              <label className="flex items-start gap-2 text-sm text-[#2e4030]">
                <input type="checkbox" name="agree" checked={form.agree} onChange={onChange} data-testid="recruiter-signup-agree" className="mt-1 accent-[#0B5E2A]" />
                <span>I agree to the initial terms and conditions and consent to Heritage Community College processing my application. I understand my account will require approval before access is granted.</span>
              </label>
              <label className="flex items-start gap-2 text-sm text-[#2e4030]">
                <input type="checkbox" name="verified" checked={form.verified} onChange={onChange} data-testid="recruiter-signup-verified" className="mt-1 accent-[#0B5E2A]" />
                <span>I confirm that all information provided above is accurate and complete, and that the documents uploaded are authentic.</span>
              </label>
            </div>

            <div>
              <button
                type="submit"
                disabled={busy}
                data-testid="recruiter-signup-submit"
                className="btn-green inline-flex items-center gap-2 px-6 py-3 rounded-md disabled:opacity-60"
              >
                {busy ? 'Submitting...' : 'Submit Application'} <ArrowRight className="h-4 w-4" />
              </button>
              <p className="mt-4 text-sm text-[#6b7e6d]">Already a partner? Contact us to request portal access.</p>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
};

const Field = ({ label, name, type = 'text', value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-[#2e4030] mb-1.5">{label}</label>
    <input name={name} type={type} value={value} onChange={onChange} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30 text-sm" />
  </div>
);

export default RecruiterSignup;
